// VehicleDetail rebuild — Airbnb listing structure, Vauren design language.
// Strict dependency whitelist; no GSAP, no helmet, no 3D.
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { transformImage } from "@/lib/imageTransform";
import { saveBookingIntent } from "@/lib/booking-intent";
import { useBookingAvailability } from "@/hooks/useBookingAvailability";
import { trackInitiateCheckout, trackViewContent } from "@/lib/conversion-pixels";
import { PageLayout } from "@/components/PageLayout";
import { SEOHead } from "@/components/SEOHead";
import VehicleReviews from "@/components/marketplace/VehicleReviews";
import HostTrustSignals from "@/components/marketplace/HostTrustSignals";
import { STANDARD_SERVICES, AMENITY_CATEGORIES } from "@/lib/listing-catalog";
import SaveVehicleButton from "@/components/marketplace/SaveVehicleButton";
import TrustBadges, { computeHostBadges } from "@/components/marketplace/TrustBadges";
import SocialShareButtons from "@/components/marketplace/SocialShareButtons";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Breadcrumb from "@/components/Breadcrumb";
import { format, differenceInDays } from "date-fns";
import { describePolicy } from "@/lib/cancellationPolicy";
import { getServiceFee, getSecurityDeposit, getMultiDayDiscount, VAUREN_SERVICE_FEE_RATE } from "@/lib/rental-constants";
import { motion } from "framer-motion";
import {
  MapPin,
  Star,
  Users,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  X,
  Camera,
  Key,
  Compass,
  RotateCcw,
  Flag,
  MessageSquare,
  Clock,
  ArrowUpRight,
  Truck,
  Map,
  Zap,
  Package,
  Route,
  Heart,
  CheckCircle,
  Bell,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";

// Mapbox public access token. Set as VITE_MAPBOX_TOKEN in Vercel env vars.
// If absent, Section U falls back to a static placeholder so the page never
// crashes, and a warning is logged once so it's visible in dev tools.
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
if (!MAPBOX_TOKEN && typeof window !== "undefined") {
  console.warn(
    "[VehicleDetail] VITE_MAPBOX_TOKEN missing — map section will render placeholder."
  );
}

const PALETTE = {
  bg: "#F5F0EB",
  bgInset: "#F8F4F0",
  text: "#2C2420",
  textMuted: "#4F413E",
  textTaupe: "#9C9088",
  border: "#E0D8D2",
  accent: "#C8BFB8",
  bronze: "#997E54",
  bronzeDark: "#997E54",
  surface: "#FFFFFF",
} as const;

const SERIF = '"Cormorant Garamond", serif';
const SANS = '"DM Sans", system-ui, sans-serif';

const formatCurrency = (n: number | null | undefined) => {
  if (n == null) return "$0";
  return n % 1 === 0 ? `$${n.toLocaleString()}` : `$${n.toFixed(2)}`;
};

const titleCase = (s: string | null | undefined) => {
  if (!s) return "";
  return s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

// First word of a display name, capitalised. "Hosted by {firstName(...)}"
// renders as "Hosted by Collin" for "Collin Skees", or just "the host"
// when display_name is empty/null.
const firstName = (s: string | null | undefined) => {
  if (!s) return "";
  const first = s.trim().split(/\s+/)[0] || "";
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "";
};

// Two-letter initials for the bronze fallback circle: "Collin Skees" → CS,
// "Madonna" → M. Returns "" when the input is missing so callers can
// render a generic icon instead.
const initials = (s: string | null | undefined) => {
  if (!s) return "";
  const parts = s.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

interface Vehicle {
  id: string;
  title: string;
  make: string;
  model: string;
  year: number;
  description: string | null;
  listing_story: string | null;
  daily_price: number;
  weekend_price: number | null;
  city: string;
  state: string;
  passenger_capacity: number;
  seat_count: number | null;
  luggage_capacity: number | null;
  transmission: string | null;
  drivetrain: string | null;
  body_style: string | null;
  vehicle_type: string;
  vauren_tier: string | null;
  exterior_color: string | null;
  interior_color: string | null;
  horsepower: number | null;
  booking_mode: string;
  pickup_type: string;
  cancellation_policy: string;
  custom_cancellation_policy: {
    type: "custom";
    name: string;
    full_refund_hours: number;
    partial_refund_hours: number;
    partial_refund_pct: number;
  } | null;
  cleaning_fee: number;
  late_return_fee: number;
  security_deposit: number;
  extra_mileage_fee: number;
  included_mileage: number | null;
  unlimited_mileage: boolean;
  age_requirement: number | null;
  chauffeur_available: boolean;
  delivery_available: boolean | null;
  delivery_option: "pickup_only" | "delivery_available" | "both" | null;
  delivery_radius_miles: number | null;
  delivery_fee: number | null;
  delivery_cities: string[] | null;
  delivery_notes: string | null;
  self_drive_allowed: boolean | null;
  hide_exact_address: boolean | null;
  latitude: number | null;
  longitude: number | null;
  owner_id: string;
}

interface VehiclePhoto {
  photo_url: string;
  is_primary: boolean;
  sort_order: number;
}

interface OwnerProfile {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  average_rating: number | null;
  total_reviews: number;
  completed_trips: number;
  is_verified: boolean;
  created_at: string;
}

const Skeleton = () => (
  <PageLayout>
    <div style={{ background: PALETTE.bg, minHeight: "100vh", padding: "32px 0" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ height: 28, width: "40%", background: PALETTE.bgInset, borderRadius: 4, marginBottom: 16 }} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gridTemplateRows: "1fr 1fr",
            gap: 8,
            aspectRatio: "2 / 1",
            marginBottom: 32,
          }}
        >
          <div style={{ gridColumn: "1 / 2", gridRow: "1 / 3", background: PALETTE.bgInset, borderRadius: "12px 0 0 12px" }} />
          <div style={{ background: PALETTE.bgInset }} />
          <div style={{ background: PALETTE.bgInset, borderRadius: "0 12px 0 0" }} />
          <div style={{ background: PALETTE.bgInset }} />
          <div style={{ background: PALETTE.bgInset, borderRadius: "0 0 12px 0" }} />
        </div>
      </div>
    </div>
  </PageLayout>
);

const ErrorState = ({ message }: { message: string }) => (
  <PageLayout>
    <div
      style={{
        background: PALETTE.bg,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div
          style={{
            fontFamily: SERIF,
            fontSize: 28,
            color: PALETTE.text,
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          {message}
        </div>
        <Link
          to="/marketplace/rent"
          style={{
            display: "inline-block",
            marginTop: 16,
            padding: "12px 24px",
            background: PALETTE.bronze,
            color: PALETTE.surface,
            fontFamily: SANS,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            textDecoration: "none",
            borderRadius: 4,
          }}
        >
          Browse all listings
        </Link>
      </div>
    </div>
  </PageLayout>
);

interface LightboxProps {
  photos: VehiclePhoto[];
  startIndex: number;
  onClose: () => void;
}

const Lightbox = ({ photos, startIndex, onClose }: LightboxProps) => {
  const [index, setIndex] = useState(startIndex);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, photos.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [photos.length, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,16,14,0.95)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close gallery"
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          background: "rgba(255,255,255,0.1)",
          border: "none",
          color: PALETTE.surface,
          width: 44,
          height: 44,
          borderRadius: "50%",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <X size={22} />
      </button>
      <div
        style={{ position: "absolute", top: 24, left: 24, color: PALETTE.surface, fontFamily: SANS, fontSize: 13 }}
      >
        {index + 1} of {photos.length}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIndex((i) => Math.max(i - 1, 0));
        }}
        disabled={index === 0}
        aria-label="Previous"
        style={{
          position: "absolute",
          left: 24,
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.1)",
          border: "none",
          color: PALETTE.surface,
          width: 48,
          height: 48,
          borderRadius: "50%",
          cursor: index === 0 ? "default" : "pointer",
          opacity: index === 0 ? 0.3 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIndex((i) => Math.min(i + 1, photos.length - 1));
        }}
        disabled={index === photos.length - 1}
        aria-label="Next"
        style={{
          position: "absolute",
          right: 24,
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.1)",
          border: "none",
          color: PALETTE.surface,
          width: 48,
          height: 48,
          borderRadius: "50%",
          cursor: index === photos.length - 1 ? "default" : "pointer",
          opacity: index === photos.length - 1 ? 0.3 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ChevronRight size={24} />
      </button>
      <img
        src={transformImage(photos[index]?.photo_url, { width: 1600, quality: 85, resize: "contain" })}
        alt={`Photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        loading="eager"
        decoding="async"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
        }}
      />
    </motion.div>
  );
};

const Divider = () => (
  <div
    style={{
      height: 1,
      background: `linear-gradient(90deg, transparent, ${PALETTE.border} 50%, transparent)`,
      margin: "48px 0",
    }}
  />
);

// Calendar contrast overrides — scoped to <div className="vauren-calendar">.
// shadcn's Calendar passes through to react-day-picker which applies the
// rdp-* class names this stylesheet targets.
const CALENDAR_OVERRIDES = `
/* ── Month caption (e.g. "May 2026") ── */
.vauren-calendar .rdp-caption_label,
.vauren-calendar [class*="rdp-caption_label"] {
  font-family: 'Cormorant Garamond', serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #2C2420 !important;
  letter-spacing: -0.005em !important;
}

/* ── Day-of-week headers (Su Mo Tu...) ── */
.vauren-calendar .rdp-head_cell,
.vauren-calendar [class*="rdp-head_cell"] {
  color: #2C2420 !important;
  font-weight: 600 !important;
  font-family: 'DM Sans', system-ui, sans-serif !important;
  text-transform: none !important;
}

/* ── Month nav arrows (prev / next) ── */
.vauren-calendar .rdp-nav_button,
.vauren-calendar [class*="rdp-nav_button"],
.vauren-calendar button[name="previous-month"],
.vauren-calendar button[name="next-month"] {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid #E0D8D2 !important;
  border-radius: 6px !important;
  color: #2C2420 !important;
  width: 32px !important;
  height: 32px !important;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease !important;
}
.vauren-calendar .rdp-nav_button:hover,
.vauren-calendar [class*="rdp-nav_button"]:hover,
.vauren-calendar button[name="previous-month"]:hover,
.vauren-calendar button[name="next-month"]:hover {
  background: #997E54 !important;
  border-color: #997E54 !important;
  color: #FFFFFF !important;
}

/* ── Day number base (all unselected days) — explicit colour so cream-on-
   cream blending cannot happen even with shadcn defaults underneath ── */
.vauren-calendar .rdp-day,
.vauren-calendar [class*="rdp-day"] {
  color: #2C2420 !important;
  font-family: "DM Sans", system-ui, sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  border-radius: 6px !important;
}

/* ── Hover (only when not disabled / booked) ── */
.vauren-calendar .rdp-day:hover:not([disabled]):not(.rdp-day_disabled):not(.rdp-day_booked),
.vauren-calendar [class*="rdp-day"]:hover:not([disabled]):not([class*="rdp-day_disabled"]):not(.rdp-day_booked) {
  background: rgba(153, 126, 84, 0.10) !important;
  color: #2C2420 !important;
  opacity: 1 !important;
}

/* ── Today ── */
.vauren-calendar .rdp-day_today,
.vauren-calendar [class*="rdp-day_today"] {
  font-weight: 700 !important;
  color: #997E54 !important;
  text-decoration: underline !important;
  text-underline-offset: 3px !important;
}

/* ── Disabled / past days (NOT booked) — faded grey, no strikethrough.
   The ":not(.rdp-day_booked)" is the load-bearing piece: the previous
   selector used [data-booked="true"], an attribute react-day-picker
   never sets, so booked dates inherited this rule and rendered as
   plain grey at 0.5 opacity instead of the warm-red tint below. ── */
.vauren-calendar .rdp-day_disabled:not(.rdp-day_booked),
.vauren-calendar [class*="rdp-day_disabled"]:not(.rdp-day_booked) {
  color: #C8BFB8 !important;
  text-decoration: none !important;
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

/* ── BOOKED / unavailable dates — warm red so they read as "taken"
   at a glance instead of looking the same as past-grey. Bumped the
   alpha so the text passes WCAG AA on the cream calendar surface. ── */
.vauren-calendar .rdp-day_booked,
.vauren-calendar [class*="rdp-day_booked"] {
  background: rgba(220, 80, 60, 0.10) !important;
  color: rgba(155, 40, 30, 0.85) !important;
  border: 1px solid rgba(220, 80, 60, 0.30) !important;
  border-radius: 6px !important;
  text-decoration: none !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

/* ── Hover on booked dates — slightly deeper tint, no selection. ── */
.vauren-calendar .rdp-day_booked:hover,
.vauren-calendar [class*="rdp-day_booked"]:hover {
  background: rgba(220, 80, 60, 0.18) !important;
}

/* ── Range start / end ── */
.vauren-calendar .rdp-day_range_start,
.vauren-calendar .rdp-day_range_end,
.vauren-calendar [class*="rdp-day_range_start"],
.vauren-calendar [class*="rdp-day_range_end"] {
  background: ${PALETTE.bronze} !important;
  color: ${PALETTE.surface} !important;
  font-weight: 600 !important;
}

/* ── Range middle ── */
.vauren-calendar .rdp-day_range_middle,
.vauren-calendar [class*="rdp-day_range_middle"] {
  background: rgba(153, 126, 84, 0.25) !important;
  color: #2C2420 !important;
}

/* ── Generic selected (single-mode safety) ── */
.vauren-calendar .rdp-day_selected:not(.rdp-day_range_middle),
.vauren-calendar [class*="rdp-day_selected"]:not([class*="rdp-day_range_middle"]) {
  background: ${PALETTE.bronze} !important;
  color: ${PALETTE.surface} !important;
}
`;

// Mobile (< 768px) responsive overrides. Inline-style components can't use
// media queries, so we target them by className from a stylesheet.
const RESPONSIVE_CSS = `
@media (max-width: 768px) {
  .vd-section {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
  /* Phones get the hero only — the 4-thumb strip below it is
     decorative and tapping the hero opens the full lightbox anyway. */
  .vd-photo-secondary-strip { display: none !important; }
  .vd-photo-tile-secondary { display: none !important; }
  .vd-layout {
    grid-template-columns: 1fr !important;
    gap: 24px !important;
    padding-top: 24px !important;
  }
  .vd-booking-widget {
    position: relative !important;
    top: auto !important;
    max-height: none !important;
    overflow: visible !important;
    order: -1 !important;
    margin-bottom: 24px !important;
  }
  .vd-specs-strip { gap: 16px 0 !important; }
  .vd-specs-strip > div { width: 50% !important; }
  .vd-map-container {
    height: 300px !important;
    min-height: 300px !important;
    display: block !important;
    visibility: visible !important;
  }
  .vd-where-section {
    display: block !important;
  }
  .vd-explore-grid {
    grid-template-columns: 1fr !important;
    gap: 0 !important;
  }
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

const Section = ({
  title,
  subtitle,
  children,
}: {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <motion.section
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.5, ease: "easeOut" }}
  >
    {title && (
      <h2
        style={{
          fontFamily: SERIF,
          fontSize: 24,
          fontWeight: 600,
          color: PALETTE.text,
          margin: 0,
          marginBottom: subtitle ? 6 : 16,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
    )}
    {subtitle && (
      <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.textMuted, marginBottom: 16 }}>
        {subtitle}
      </div>
    )}
    {children}
  </motion.section>
);

const VehicleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [photos, setPhotos] = useState<VehiclePhoto[]>([]);
  const [owner, setOwner] = useState<OwnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vehicle) return;
    // META PIXEL: ViewContent — verify in Meta Events Manager > Test Events
    trackViewContent({
      content_name: vehicle.title,
      content_ids: [vehicle.id],
      content_type: "vehicle",
      value: vehicle.daily_price,
      currency: "USD",
    });
    // Fire only when the vehicle identity changes; other fields are
    // immutable for the lifetime of a given vehicle id, so depending on
    // the full object would re-fire on incidental re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.id]);

  // Seed the booking-widget date range from the URL on mount so a
  // renter arriving from /rent-a-van with ?start=&end= sees the dates
  // they searched for already filled in — no re-entry. Falsy params or
  // invalid date strings fall through to the unset state.
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const start = searchParams.get("start");
    const end = searchParams.get("end");
    if (!start || !end) return undefined;
    const from = new Date(start);
    const to = new Date(end);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return undefined;
    return { from, to };
  });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  // Simulated concurrent viewer count — fixed for the lifetime of this mount
  // so the number doesn't flicker between renders. 2–5 range; only shown when
  // ≥3 so it never feels artificial when low.
  const viewerCount = useMemo(() => Math.floor(Math.random() * 4) + 2, []);
  const [confirmedBookingCount, setConfirmedBookingCount] = useState<number>(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  // Hero carousel index — drives the photo shown on the primary tile.
  // Increments via mobile swipe gestures; arrow buttons / lightbox
  // remain the only entry points on desktop.
  const [heroIndex, setHeroIndex] = useState(0);
  const heroTouchStartX = useRef<number | null>(null);
  const heroTouchStartY = useRef<number | null>(null);
  const heroTouchDeltaX = useRef<number>(0);
  const heroSwipedAt = useRef<number>(0);
  const [features, setFeatures] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messageSending, setMessageSending] = useState(false);

  // Map container — callback ref via useState. Storing the DOM node in
  // state means the init effect re-runs once the div actually mounts.
  // The previous useRef pattern fired the effect before the conditional
  // render attached the node, leaving the container null on first run.
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const [hoveredSpot, setHoveredSpot] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);

      // Server-side ORDER BY on the joined relation. The
      // vehicle_photos_one_primary partial unique index guarantees a
      // single is_primary=true row per vehicle, so the first element is
      // always the primary — no JS sort needed.
      const { data: v, error: vErr } = await supabase
        .from("vehicles")
        .select("*, vehicle_photos(photo_url, is_primary, sort_order)")
        .eq("id", id)
        .eq("is_active", true)
        .eq("is_approved", true)
        .order("is_primary", { referencedTable: "vehicle_photos", ascending: false })
        .order("sort_order", { referencedTable: "vehicle_photos", ascending: true })
        .maybeSingle();

      if (cancelled) return;

      if (vErr || !v) {
        setError("This listing is no longer available");
        setLoading(false);
        return;
      }

      const orderedPhotos: VehiclePhoto[] = (v as any).vehicle_photos || [];

      const { vehicle_photos: _ignore, ...vehicleRow } = v as any;
      setVehicle(vehicleRow as Vehicle);
      setPhotos(orderedPhotos);

      // Only select columns that actually exist on owner_profiles. The
      // earlier version asked for response_time_minutes / response_rate /
      // joined_at / languages — none of those exist in the schema, so
      // Supabase returned a 400 and `o` stayed null. That's what made the
      // host card render "Vauren host" with no headshot for every listing.
      const { data: o, error: oErr } = await supabase
        .from("owner_profiles")
        .select(
          "user_id, display_name, avatar_url, bio, city, state, completed_trips, average_rating, total_reviews, is_verified, created_at"
        )
        .eq("user_id", (v as any).owner_id)
        .maybeSingle();

      if (cancelled) return;
      if (oErr) {
        console.warn("[VehicleDetail] owner profile fetch failed", oErr);
      }
      setOwner((o as OwnerProfile) || null);

      // Load amenity features (one row per amenity in vehicle_features).
      const { data: featureRows } = await supabase
        .from("vehicle_features")
        .select("feature")
        .eq("vehicle_id", id);
      if (!cancelled && featureRows) {
        setFeatures(featureRows.map((r: any) => r.feature as string).filter(Boolean));
      }

      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Track the current authenticated user so the Message Host modal can gate
  // its CTA (sign in first if anonymous, otherwise open inline composer).
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      setCurrentUser(data.user ? { id: data.user.id } : null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Availability is owned by the shared useBookingAvailability hook —
  // it queries rental_bookings (incl. awaiting_payment so a renter
  // mid-Stripe-checkout holds the dates), availability_blocks, and
  // calendar_events, then subscribes to realtime changes on all three
  // so the calendar greys out new conflicts without a reload. The hook
  // is the same one BookVehicle uses for its pre-submit guard, so the
  // detail page and the booking page can never disagree about which
  // days are available.
  const { isDayUnavailable: isDateBooked } = useBookingAvailability(vehicle?.id);

  const handleDayClick = (
    _day: Date,
    modifiers: { booked?: boolean; disabled?: boolean }
  ) => {
    if (modifiers.booked || modifiers.disabled) {
      toast.message("These dates are unavailable", {
        description: "This van is already booked for the selected dates. Please choose different dates.",
      });
    }
  };

  // Mapbox initialisation. Dynamic-imports mapbox-gl + its CSS so the
  // Confirmed-booking count for social-proof urgency ("Booked N times").
  useEffect(() => {
    if (!vehicle?.id) return;
    let cancelled = false;
    supabase
      .from("rental_bookings")
      .select("id", { count: "exact", head: true })
      .eq("vehicle_id", vehicle.id)
      .eq("status", "confirmed")
      .then(({ count }) => {
        if (!cancelled && typeof count === "number") setConfirmedBookingCount(count);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicle?.id]);

  // Recently-viewed tracker — push the current vehicle id to localStorage so
  // RentAVan can surface a "Recently viewed" strip across sessions. Cap at 6.
  useEffect(() => {
    if (!vehicle?.id) return;
    try {
      const key = "vauren_recently_viewed";
      const existing = JSON.parse(localStorage.getItem(key) || "[]") as string[];
      const updated = [vehicle.id, ...existing.filter((id) => id !== vehicle.id)].slice(0, 6);
      localStorage.setItem(key, JSON.stringify(updated));
    } catch {
      // localStorage may be unavailable in private mode — silently skip.
    }
  }, [vehicle?.id]);

  // ~800 KB / 200 KB-gzip library only loads on this route, not in the
  // main entry bundle. Uses vehicle.latitude/longitude when present;
  // otherwise geocodes city + state via Mapbox once. No-ops if token is
  // missing.
  useEffect(() => {
    // Token sanity check — a valid public token starts with pk.eyJ and is
    // ~180 chars. Anything shorter / wrongly prefixed is the real reason
    // tiles fail to load.
    console.log("[Map debug]", {
      hasToken: !!MAPBOX_TOKEN,
      prefix: MAPBOX_TOKEN?.slice(0, 12),
      length: MAPBOX_TOKEN?.length,
      hasContainer: !!mapContainer,
      vehicleCity: vehicle?.city,
      vehicleState: vehicle?.state,
      vehicleLat: vehicle?.latitude,
      vehicleLng: vehicle?.longitude,
    });
    if (!MAPBOX_TOKEN || !vehicle || !mapContainer) return;

    let map: any = null;
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    (async () => {
      const [{ default: mapboxgl }] = await Promise.all([
        import("mapbox-gl"),
        import("mapbox-gl/dist/mapbox-gl.css"),
      ]);

      if (cancelled || !mapContainer) return;

      const initMap = (lng: number, lat: number) => {
        // Defer the actual map creation by ~150ms. The parent grid can be
        // mid-layout when this effect first fires; if mapbox-gl reads a
        // zero-rect container, the canvas renders blank under the marker.
        // Pairing this with the ResizeObserver below covers both initial
        // measurement and any later layout shifts.
        setTimeout(() => {
          if (cancelled || !mapContainer) return;
          mapboxgl.accessToken = MAPBOX_TOKEN;

          map = new mapboxgl.Map({
            container: mapContainer,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [lng, lat],
            zoom: 12,
            attributionControl: false,
            scrollZoom: false,
            dragPan: true,
            doubleClickZoom: true,
          });

          // Surface mapbox-gl errors (404 style, 401 token, tile fetch) to
          // the console so blank-map issues stop being silent.
          map.on("error", (e: any) => {
            console.error("[Mapbox error]", e?.error || e);
          });

          map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
          // Re-enable scroll zoom only after explicit user interaction.
          map.once("click", () => map?.scrollZoom.enable());

          // Custom Vauren marker — black circle with white van silhouette.
          const markerEl = document.createElement("div");
          markerEl.style.cssText =
            "width:56px;height:56px;background:#2C2420;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(44,36,32,0.3);cursor:pointer;";
          markerEl.innerHTML =
            '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M3 9l3-5h12l3 5v8a1 1 0 0 1-1 1h-1a2 2 0 0 1-4 0H9a2 2 0 0 1-4 0H4a1 1 0 0 1-1-1V9z"/>' +
            '<line x1="3" y1="9" x2="21" y2="9"/>' +
            '<circle cx="7" cy="17" r="2" fill="#2C2420"/>' +
            '<circle cx="17" cy="17" r="2" fill="#2C2420"/>' +
            "</svg>";

          new mapboxgl.Marker(markerEl, { anchor: "center" })
            .setLngLat([lng, lat])
            .addTo(map);

          // Privacy-radius circle — translucent bronze halo around the marker.
          map.on("load", () => {
            if (!map) return;
            // Some layouts (sticky containers, late-mounted parent grids) cause
            // mapbox-gl's first sizing pass to use a stale rect. Resize after
            // load so the canvas always matches the visible container.
            map.resize();
            map.addSource("privacy-radius", {
              type: "geojson",
              data: {
                type: "Feature",
                geometry: { type: "Point", coordinates: [lng, lat] },
                properties: {},
              },
            });
            map.addLayer({
              id: "privacy-radius-fill",
              type: "circle",
              source: "privacy-radius",
              paint: {
                "circle-radius": {
                  stops: [
                    [10, 40],
                    [15, 200],
                  ],
                },
                "circle-color": "#997E54",
                "circle-opacity": 0.12,
                "circle-stroke-width": 1,
                "circle-stroke-color": "#997E54",
                "circle-stroke-opacity": 0.3,
              },
            });
          });

          // Re-measure the canvas whenever the container actually resizes.
          // Covers the case where the container starts at 0px (sticky parent
          // not yet laid out) and grows after data arrives.
          resizeObserver = new ResizeObserver(() => {
            map?.resize();
          });
          resizeObserver.observe(mapContainer);
        }, 150);
      };

      // Dallas fallback so the map always renders when a token is present.
      const DALLAS_FALLBACK: [number, number] = [-96.797, 32.776];

      if (vehicle.latitude != null && vehicle.longitude != null) {
        initMap(vehicle.longitude, vehicle.latitude);
      } else if (vehicle.city && vehicle.state) {
        const q = encodeURIComponent(`${vehicle.city},${vehicle.state}`);
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${q}.json?access_token=${MAPBOX_TOKEN}&limit=1`;
        try {
          const r = await fetch(url);
          const data = await r.json();
          if (cancelled) return;
          const center = data?.features?.[0]?.center;
          if (Array.isArray(center) && center.length === 2) {
            initMap(center[0], center[1]);
          } else {
            console.warn("[Map geocode empty]", { city: vehicle.city, state: vehicle.state, data });
            initMap(DALLAS_FALLBACK[0], DALLAS_FALLBACK[1]);
          }
        } catch (err) {
          console.error("[Map geocode error]", err);
          if (!cancelled) initMap(DALLAS_FALLBACK[0], DALLAS_FALLBACK[1]);
        }
      } else {
        // No coordinates and no city/state — still render the map at Dallas
        // so the section never goes blank.
        initMap(DALLAS_FALLBACK[0], DALLAS_FALLBACK[1]);
      }
    })();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      map?.remove();
    };
  }, [vehicle, mapContainer]);

  if (loading) return <Skeleton />;
  if (error || !vehicle) return <ErrorState message={error || "Listing not found"} />;

  const days =
    dateRange?.from && dateRange?.to ? Math.max(1, differenceInDays(dateRange.to, dateRange.from)) : 0;
  const subtotal = days * vehicle.daily_price;
  const discountFraction = days > 0 ? getMultiDayDiscount(days) : 0;
  const discountPercent = Math.round(discountFraction * 100);
  const discountAmount = Math.round(subtotal * discountFraction * 100) / 100;
  // Service fee is computed off the raw subtotal, matching BookVehicle.tsx,
  // so the published 8% rate doesn't appear to erode when a multi-day
  // discount applies.
  const serviceFee = days > 0 ? getServiceFee(subtotal) : 0;
  const securityDeposit = days > 0 ? getSecurityDeposit(vehicle.vehicle_type || "sprinter_van") : 0;
  const total =
    days > 0 ? subtotal - discountAmount + vehicle.cleaning_fee + serviceFee + securityDeposit : 0;

  const description = vehicle.description || "";
  const descriptionTruncated = description.length > 380;
  const visibleDescription =
    !descriptionTruncated || showFullDescription ? description : description.slice(0, 380).trimEnd() + "…";

  const hostBadges = owner ? computeHostBadges(owner) : [];
  const memberSince = owner?.created_at ? format(new Date(owner.created_at), "MMMM yyyy") : "";

  // Five tiles for the Airbnb grid
  const galleryTiles = Array.from({ length: 5 }).map((_, i) => photos[i] || null);

  const amenities: { icon: React.ElementType; label: string }[] = [];
  if (vehicle.transmission) amenities.push({ icon: Compass, label: titleCase(vehicle.transmission) + " transmission" });
  if (vehicle.drivetrain) amenities.push({ icon: Compass, label: titleCase(vehicle.drivetrain) });
  if (vehicle.horsepower) amenities.push({ icon: Compass, label: `${vehicle.horsepower} hp` });
  if (vehicle.body_style) amenities.push({ icon: Compass, label: titleCase(vehicle.body_style) });
  if (vehicle.vauren_tier) amenities.push({ icon: Star, label: `Vauren ${titleCase(vehicle.vauren_tier)}` });
  if (vehicle.booking_mode === "instant") amenities.push({ icon: Key, label: "Instant book" });
  if (vehicle.delivery_available) amenities.push({ icon: MapPin, label: "Delivery available" });
  if (vehicle.self_drive_allowed) amenities.push({ icon: Key, label: "Self drive permitted" });
  if (vehicle.unlimited_mileage) amenities.push({ icon: Compass, label: "Unlimited mileage" });
  if (vehicle.pickup_type) amenities.push({ icon: MapPin, label: `${titleCase(vehicle.pickup_type)} pickup` });
  if (vehicle.exterior_color) amenities.push({ icon: Flag, label: `${titleCase(vehicle.exterior_color)} exterior` });
  if (vehicle.interior_color) amenities.push({ icon: Flag, label: `${titleCase(vehicle.interior_color)} interior` });

  const visibleAmenities = showAllAmenities ? amenities : amenities.slice(0, 8);

  const specStrip: { value: string; label: string; icon?: React.ElementType }[] = [];
  if (vehicle.body_style) specStrip.push({ value: titleCase(vehicle.body_style), label: "Body" });
  if (vehicle.transmission) specStrip.push({ value: titleCase(vehicle.transmission), label: "Transmission" });
  if (vehicle.drivetrain) specStrip.push({ value: titleCase(vehicle.drivetrain), label: "Drivetrain" });
  if (vehicle.horsepower) specStrip.push({ value: `${vehicle.horsepower} hp`, label: "Power" });
  specStrip.push({ value: String(vehicle.passenger_capacity), label: "Passengers" });
  if (vehicle.seat_count) specStrip.push({ value: String(vehicle.seat_count), label: "Seatbelts" });
  if (vehicle.luggage_capacity) specStrip.push({ value: String(vehicle.luggage_capacity), label: "Luggage" });
  if (owner?.completed_trips && owner.completed_trips > 0) {
    specStrip.push({ value: String(owner.completed_trips), label: "Trips Hosted", icon: Truck });
  }

  // Highlight tiles
  const highlights: { icon: React.ElementType; title: string; subtitle: string }[] = [
    {
      icon: Key,
      title: "Self check-in",
      subtitle: "Collect the keys yourself with the host's smart code.",
    },
    {
      icon: Compass,
      title: "Designed for adventure",
      subtitle: "Outfitted for long-distance travel with refined comforts onboard.",
    },
    {
      icon: RotateCcw,
      title: "Free cancellation",
      subtitle: `${titleCase(vehicle.cancellation_policy)} cancellation up to 48 hours before your trip.`,
    },
  ];

  const pickupCity = `${vehicle.city}, ${vehicle.state}`;

  return (
    <PageLayout>
      <SEOHead
        title={`${vehicle.title} | Vauren`}
        description={vehicle.description?.slice(0, 160) || `Rent ${vehicle.title} in ${pickupCity}.`}
        canonical={`/marketplace/vehicle/${vehicle.id}`}
      />

      <style>{CALENDAR_OVERRIDES}</style>
      <style>{RESPONSIVE_CSS}</style>
      <div
        style={{
          background: `linear-gradient(180deg, ${PALETTE.bg} 0%, ${PALETTE.bgInset} 100%)`,
          minHeight: "100vh",
          paddingBottom: 80,
          position: "relative",
        }}
      >
        {/* Subtle paper-grain noise overlay — pointer-events none so it never blocks UI */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
            backgroundSize: 200,
            mixBlendMode: "multiply",
            opacity: 0.6,
          }}
        />
        <div className="vd-section" style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 0", position: "relative" }}>
          <Breadcrumb
            items={[
              { label: "Marketplace", href: "/marketplace/rent" },
              { label: vehicle.city, href: `/marketplace/rent?city=${encodeURIComponent(vehicle.city)}` },
              { label: vehicle.title },
            ]}
          />
        </div>

        {/* TITLE + SHARE/SAVE ROW — relative + low z-index so the row scrolls
            cleanly beneath the fixed Header (which sits at zIndex 100). */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "16px 24px 12px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 32,
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                fontFamily: SERIF,
                fontSize: 28,
                fontWeight: 600,
                color: PALETTE.text,
                letterSpacing: "0.005em",
                margin: 0,
                marginRight: 16,
                lineHeight: 1.2,
                flex: "1 1 auto",
                minWidth: 0,
              }}
            >
              {vehicle.title}
            </h1>
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexShrink: 0 }}>
              <SocialShareButtons
                url={`/marketplace/vehicle/${vehicle.id}`}
                title={vehicle.title}
                description={vehicle.description || undefined}
              />
              <SaveVehicleButton vehicleId={vehicle.id} variant="full" />
            </div>
          </div>
        </motion.div>

        {/* LISTING STORY — short editorial pull quote rendered between
            the title and the gallery. Italic Cormorant Garamond with
            a 2px bronze left border. Renders only when the host has
            written a story for this listing. */}
        {vehicle.listing_story && vehicle.listing_story.trim() && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.05 }}
            className="vd-section"
            style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}
          >
            <blockquote
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 20,
                lineHeight: 1.55,
                letterSpacing: "0.005em",
                color: PALETTE.text,
                borderLeft: `2px solid ${PALETTE.bronze}`,
                paddingLeft: 16,
                margin: 0,
                maxWidth: 760,
              }}
            >
              {vehicle.listing_story}
            </blockquote>
          </motion.div>
        )}

        {/* PHOTO GALLERY — full-bleed hero + secondary strip.
            Layout flipped from the old 4×2 grid (primary was a square
            taking 50% width) to a flex column: full-width hero on top,
            4-thumb strip below. Secondary strip hides on mobile via
            the existing .vd-photo-tile-secondary rule + a new wrapper
            rule, so phones only see the hero. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="vd-section"
          style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              transition: "box-shadow 0.25s ease",
            }}
            onMouseOver={(e) =>
              ((e.currentTarget as HTMLDivElement).style.boxShadow = "0 12px 40px rgba(44,36,32,0.12)")
            }
            onMouseOut={(e) => ((e.currentTarget as HTMLDivElement).style.boxShadow = "none")}
          >
            {/* Primary hero — full bleed, clamp(280px, 40vh, 380px).
                cover crop biased to center 60% so the van's mid-body
                stays centred. No cream backdrop — this is the LCP
                image and most listings have one well-framed hero shot
                that benefits from edge-to-edge presentation. The
                listing-card cream-letterbox pattern is for the small
                multi-card grid where portrait uploads dominate; the
                detail page hero gets the full Turo treatment. */}
            <button
              onClick={() => {
                // Suppress the lightbox open when the click is a
                // swipe-tail rather than a real tap.
                if (Date.now() - heroSwipedAt.current < 400) return;
                if (photos.length > 0) setLightboxIndex(heroIndex);
              }}
              disabled={photos.length === 0}
              aria-label="View photo"
              className="vd-photo-tile-primary"
              onTouchStart={(e) => {
                if (photos.length < 2) return;
                heroTouchStartX.current = e.touches[0].clientX;
                heroTouchStartY.current = e.touches[0].clientY;
                heroTouchDeltaX.current = 0;
              }}
              onTouchMove={(e) => {
                if (heroTouchStartX.current == null || heroTouchStartY.current == null) return;
                const dx = e.touches[0].clientX - heroTouchStartX.current;
                const dy = e.touches[0].clientY - heroTouchStartY.current;
                // Adopt as a horizontal swipe only when the
                // horizontal axis dominates and is meaningful.
                // Otherwise let vertical scroll proceed.
                if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
                  heroTouchDeltaX.current = dx;
                }
              }}
              onTouchEnd={() => {
                if (photos.length < 2) return;
                const dx = heroTouchDeltaX.current;
                if (Math.abs(dx) >= 50) {
                  if (dx > 0) {
                    setHeroIndex((i) => (i - 1 + photos.length) % photos.length);
                  } else {
                    setHeroIndex((i) => (i + 1) % photos.length);
                  }
                  heroSwipedAt.current = Date.now();
                }
                heroTouchStartX.current = null;
                heroTouchStartY.current = null;
                heroTouchDeltaX.current = 0;
              }}
              style={{
                position: "relative",
                width: "100%",
                height: "clamp(280px, 40vh, 380px)",
                background: "#F5F0EB",
                border: "none",
                padding: 0,
                cursor: photos.length > 0 ? "pointer" : "default",
                overflow: "hidden",
                borderRadius: 12,
                touchAction: "pan-y",
              }}
            >
              {/* Photo stack — every photo mounts so swipes crossfade
                  via opacity rather than a hard src swap. transition
                  on opacity is 200ms ease per spec. */}
              {photos.map((p, i) => (
                <img
                  key={`${p.photo_url}-${i}`}
                  src={transformImage(p.photo_url, { width: 1600, quality: 85, resize: "contain" })}
                  alt={`${vehicle.title} ${i + 1}`}
                  width={1600}
                  height={1200}
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "auto"}
                  decoding={i === 0 ? "sync" : "async"}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center 60%",
                    opacity: i === heroIndex ? 1 : 0,
                    transition: "opacity 200ms ease",
                    pointerEvents: "none",
                  }}
                />
              ))}
              {/* Mobile dots — visible on touch viewports only via the
                  vd-hero-dots responsive class. Bronze active / white
                  50% inactive, max 5 dots with proportional active
                  position when more than 5 photos exist. */}
              {photos.length > 1 && (
                <div
                  className="vd-hero-dots"
                  style={{
                    position: "absolute",
                    bottom: 12,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    gap: 4,
                    pointerEvents: "none",
                    zIndex: 2,
                  }}
                >
                  {(() => {
                    const total = photos.length;
                    const dotCount = Math.min(total, 5);
                    const activeDot =
                      total <= 5
                        ? heroIndex
                        : Math.round((heroIndex / (total - 1)) * (dotCount - 1));
                    return Array.from({ length: dotCount }).map((_, i) => (
                      <div
                        key={i}
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background:
                            i === activeDot ? "#997E54" : "rgba(255,255,255,0.5)",
                          transition: "background 0.2s ease",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                        }}
                      />
                    ));
                  })()}
                </div>
              )}
            </button>

            {/* Secondary thumbnail strip — 4 tiles in a row, aspect
                4:1 so each thumb stays roughly square. Hidden on
                mobile via the responsive CSS so phones only get the
                hero. */}
            <div
              className="vd-photo-secondary-strip"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 8,
                aspectRatio: "4 / 1",
              }}
            >
              {galleryTiles.slice(1).map((tile, idx) => {
                const i = idx + 1;
                const corners: React.CSSProperties =
                  idx === 0
                    ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }
                    : idx === 3
                      ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 }
                      : {};

                return (
                  <button
                    key={i}
                    onClick={() => tile && setLightboxIndex(i)}
                    disabled={!tile}
                    aria-label="View photo"
                    className="vd-photo-tile-secondary"
                    style={{
                      position: "relative",
                      background: "#F5F0EB",
                      border: "none",
                      padding: 0,
                      cursor: tile ? "pointer" : "default",
                      overflow: "hidden",
                      ...corners,
                    }}
                  >
                    {tile && (
                      <img
                        src={transformImage(tile.photo_url, { width: 800, quality: 75, resize: "contain" })}
                        alt={`${vehicle.title} ${i + 1}`}
                        width={800}
                        height={600}
                        loading="lazy"
                        decoding="async"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center 60%",
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            {photos.length > 0 && (
              <button
                type="button"
                onClick={() => setLightboxIndex(0)}
                style={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  background: PALETTE.surface,
                  color: PALETTE.text,
                  border: `1px solid ${PALETTE.border}`,
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontFamily: SANS,
                  fontSize: 13,
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  zIndex: 2,
                }}
              >
                <Camera size={14} />
                Show all {photos.length} photos
              </button>
            )}
          </div>
        </motion.div>

        {/* TWO-COLUMN BODY: 60% / 40% */}
        <div
          className="vd-section vd-layout"
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "32px 24px 0",
            display: "grid",
            gridTemplateColumns: "minmax(0, 3fr) minmax(360px, 2fr)",
            gap: 64,
            alignItems: "start",
          }}
        >
          {/* LEFT COLUMN */}
          <div style={{ minWidth: 0 }}>
            {/* A. Type + location summary */}
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: 22,
                fontWeight: 600,
                color: PALETTE.text,
                margin: 0,
                marginBottom: 8,
                letterSpacing: "0.005em",
              }}
            >
              Luxury {titleCase(vehicle.vehicle_type)} in {pickupCity}
            </h2>

            {/* B. Inline stats with middots */}
            <div
              style={{
                fontFamily: SANS,
                fontSize: 16,
                color: PALETTE.text,
                marginBottom: 8,
              }}
            >
              {vehicle.passenger_capacity} passengers
              {vehicle.seat_count != null && ` · ${vehicle.seat_count} seats`}
              {vehicle.luggage_capacity != null && ` · ${vehicle.luggage_capacity} bags`}
            </div>

            {/* C. Star rating row */}
            {owner?.average_rating != null && owner.total_reviews > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: SANS,
                  fontSize: 14,
                  color: PALETTE.text,
                }}
              >
                <Star size={14} fill={PALETTE.bronze} color={PALETTE.bronze} />
                <span style={{ fontWeight: 600 }}>{owner.average_rating.toFixed(2)}</span>
                <span style={{ color: PALETTE.textTaupe }}>·</span>
                <span style={{ textDecoration: "underline" }}>{owner.total_reviews} reviews</span>
              </div>
            )}

            <Divider />

            {/* E. Host card — text + avatar, no card framing.
                Avatar: real headshot when owner.avatar_url is set,
                otherwise initials in a bronze circle. "Hosted by {first
                name}" reads warmer than the full legal name. */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: owner?.avatar_url ? PALETTE.bgInset : PALETTE.bronze,
                  overflow: "hidden",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: PALETTE.surface,
                  fontFamily: SANS,
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
                aria-label={owner?.display_name ? `${owner.display_name} avatar` : "Host avatar"}
              >
                {owner?.avatar_url ? (
                  <img
                    src={owner.avatar_url}
                    alt={owner.display_name}
                    loading="lazy"
                    decoding="async"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : initials(owner?.display_name) ? (
                  <span>{initials(owner?.display_name)}</span>
                ) : (
                  <Users size={20} color={PALETTE.surface} />
                )}
              </div>
              <div>
                <div style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 600, color: PALETTE.text }}>
                  Hosted by {firstName(owner?.display_name) || "your host"}
                </div>
                {memberSince && (
                  <div style={{ fontFamily: SANS, fontSize: 13, color: PALETTE.textTaupe, marginTop: 2 }}>
                    Hosting since {memberSince}
                  </div>
                )}
                <HostTrustSignals hostId={(vehicle as any).owner_id} />
              </div>
            </div>

            {/* F. Mileage policy — only renders when there's meaningful
                mileage info to show. Three states:
                  • unlimited_mileage OR included_mileage >= 9999 → "Unlimited"
                  • numeric included_mileage in (0, 9999) AND extra fee > 0 → detail
                  • everything else (null/0/sentinel only) → hide the row
                The 99999 sentinel is never shown to renters. */}
            {(() => {
              const v = vehicle as any;
              const unlimited = !!v.unlimited_mileage;
              const includedRaw = v.included_mileage;
              const included = typeof includedRaw === "number" ? includedRaw : Number(includedRaw);
              const includedIsNumber = Number.isFinite(included);
              const extraFee = Number(v.extra_mileage_fee ?? 0);

              const showUnlimited = unlimited || (includedIsNumber && included >= 9999);
              const showDetail =
                !showUnlimited &&
                includedIsNumber &&
                included > 0 &&
                included < 9999 &&
                extraFee > 0;

              if (!showUnlimited && !showDetail) return null;

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    background: PALETTE.bgInset,
                    border: `1px solid ${PALETTE.border}`,
                    borderRadius: 12,
                    marginTop: 16,
                  }}
                >
                  <Route size={18} color={PALETTE.bronze} style={{ flexShrink: 0 }} />
                  <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.text, lineHeight: 1.5 }}>
                    {showUnlimited ? (
                      <strong style={{ fontWeight: 600 }}>Unlimited miles included.</strong>
                    ) : (
                      <>
                        <strong style={{ fontWeight: 600 }}>Mileage:</strong>{" "}
                        {included} miles per day included. ${extraFee.toFixed(2)} per additional mile.
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* F.2 Delivery policy row — same chrome as Mileage. The
                copy switches on the new delivery_option enum (with a
                fallback to the legacy delivery_available boolean for
                any vehicle row created before the migration ran). */}
            {(() => {
              const v = vehicle as any;
              const option: string = v.delivery_option ?? (v.delivery_available ? "delivery_available" : "pickup_only");
              const fee = Number(v.delivery_fee ?? 0);
              const cities: string[] = Array.isArray(v.delivery_cities) ? v.delivery_cities : [];
              const cityList = cities.length > 0 ? cities.slice(0, 4).join(", ") + (cities.length > 4 ? "…" : "") : null;
              const pickupAddress = v.pickup_address || pickupCity;

              let body: React.ReactNode = null;
              if (option === "pickup_only") {
                body = (
                  <>
                    <strong style={{ fontWeight: 600 }}>Pickup only.</strong>{" "}
                    {pickupAddress}
                  </>
                );
              } else if (option === "both") {
                body = (
                  <>
                    <strong style={{ fontWeight: 600 }}>Pickup or delivery available.</strong>
                    {fee > 0 && <> Delivery fee ${fee.toFixed(0)}.</>}
                    {cityList && <> Serving: {cityList}.</>}
                  </>
                );
              } else {
                body = (
                  <>
                    <strong style={{ fontWeight: 600 }}>Delivery available.</strong>
                    {fee > 0 && <> ${fee.toFixed(0)} fee.</>}
                    {cityList && <> Serving: {cityList}.</>}
                  </>
                );
              }

              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    background: PALETTE.bgInset,
                    border: `1px solid ${PALETTE.border}`,
                    borderRadius: 12,
                    marginTop: 8,
                  }}
                >
                  <Map size={18} color={PALETTE.bronze} style={{ flexShrink: 0 }} />
                  <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.text, lineHeight: 1.5 }}>
                    {body}
                    {v.delivery_notes && option !== "pickup_only" && (
                      <div style={{ fontSize: 12, color: PALETTE.textTaupe, marginTop: 4 }}>
                        {v.delivery_notes}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <Divider />

            {/* G. Three feature highlights */}
            <div style={{ display: "grid", gap: 20 }}>
              {highlights.map((h) => {
                const Icon = h.icon;
                return (
                  <div key={h.title} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <Icon size={22} color={PALETTE.bronze} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <div style={{ fontFamily: SANS, fontSize: 15, fontWeight: 600, color: PALETTE.text }}>
                        {h.title}
                      </div>
                      <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.textMuted, marginTop: 2 }}>
                        {h.subtitle}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Divider />

            {/* I. Description prose */}
            <p
              style={{
                fontFamily: SANS,
                fontSize: 16,
                color: PALETTE.text,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                margin: 0,
              }}
            >
              {visibleDescription || "No description provided."}
            </p>
            {descriptionTruncated && (
              <button
                onClick={() => setShowFullDescription((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginTop: 12,
                  color: PALETTE.text,
                  fontFamily: SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {showFullDescription ? "Show less" : "Show more"}
              </button>
            )}

            {/* J. Services offered — compact icon + label grid. Reads
                vehicles.services_offered (text[] of STANDARD_SERVICES
                keys). */}
            {Array.isArray((vehicle as any).services_offered) &&
              (vehicle as any).services_offered.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <p
                      style={{
                        fontFamily: SERIF,
                        fontSize: 22,
                        fontWeight: 500,
                        color: PALETTE.text,
                        margin: "0 0 12px",
                        letterSpacing: "0.01em",
                      }}
                    >
                      Services
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 10,
                      }}
                      className="lg:!grid-cols-3"
                    >
                      {STANDARD_SERVICES.filter((s) =>
                        ((vehicle as any).services_offered as string[]).includes(s.key),
                      ).map(({ key, label, Icon }) => (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 10px",
                            background: PALETTE.bgInset,
                            border: `1px solid ${PALETTE.border}`,
                            borderRadius: 8,
                            fontFamily: SANS,
                            fontSize: 13,
                            color: PALETTE.text,
                          }}
                        >
                          <Icon size={14} color={PALETTE.bronze} style={{ flexShrink: 0 }} />
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

            {/* K. Amenities — categorised. Reads vehicle_features and
                buckets each row into the matching AMENITY_CATEGORIES
                section. Items not in any category fall under "More". */}
            {features.length > 0 && (
              <>
                <Divider />
                <div>
                  <p
                    style={{
                      fontFamily: SERIF,
                      fontSize: 22,
                      fontWeight: 500,
                      color: PALETTE.text,
                      margin: "0 0 16px",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Amenities
                  </p>
                  <div style={{ display: "grid", gap: 18 }}>
                    {(() => {
                      const selected = new Set(features);
                      const categorised = AMENITY_CATEGORIES.map((cat) => ({
                        ...cat,
                        items: cat.items.filter((i) => selected.has(i.value)),
                      })).filter((cat) => cat.items.length > 0);
                      const knownValues = new Set(
                        AMENITY_CATEGORIES.flatMap((c) => c.items.map((i) => i.value)),
                      );
                      const uncategorised = features.filter((f) => !knownValues.has(f));
                      return (
                        <>
                          {categorised.map((cat) => (
                            <div key={cat.key}>
                              <p
                                style={{
                                  fontFamily: SANS,
                                  fontSize: 11,
                                  letterSpacing: "0.14em",
                                  textTransform: "uppercase",
                                  fontWeight: 600,
                                  color: PALETTE.textMuted,
                                  margin: "0 0 8px",
                                }}
                              >
                                {cat.label}
                              </p>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                  gap: 8,
                                }}
                                className="lg:!grid-cols-3"
                              >
                                {cat.items.map((it) => {
                                  const Icon = it.Icon;
                                  return (
                                    <div
                                      key={it.value}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "6px 10px",
                                        background: PALETTE.bgInset,
                                        border: `1px solid ${PALETTE.border}`,
                                        borderRadius: 6,
                                        fontFamily: SANS,
                                        fontSize: 12.5,
                                        color: PALETTE.text,
                                      }}
                                    >
                                      {Icon && (
                                        <Icon size={12} color={PALETTE.bronze} style={{ flexShrink: 0 }} />
                                      )}
                                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {it.label}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                          {uncategorised.length > 0 && (
                            <div>
                              <p
                                style={{
                                  fontFamily: SANS,
                                  fontSize: 11,
                                  letterSpacing: "0.14em",
                                  textTransform: "uppercase",
                                  fontWeight: 600,
                                  color: PALETTE.textMuted,
                                  margin: "0 0 8px",
                                }}
                              >
                                More
                              </p>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                  gap: 8,
                                }}
                                className="lg:!grid-cols-3"
                              >
                                {uncategorised.map((v) => (
                                  <div
                                    key={v}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "6px 10px",
                                      background: PALETTE.bgInset,
                                      border: `1px solid ${PALETTE.border}`,
                                      borderRadius: 6,
                                      fontFamily: SANS,
                                      fontSize: 12.5,
                                      color: PALETTE.text,
                                    }}
                                  >
                                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {v}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            <Divider />

            {/* J2. Perfect for — use-case cards derived from vehicle data.
                Icons are lucide-react glyphs (no emoji); brand voice prefers
                line icons over picture characters across the marketplace. */}
            {(() => {
              const uses: { icon: React.ElementType; label: string; desc: string }[] = [];
              if (vehicle.passenger_capacity >= 8) {
                uses.push({ icon: Users, label: "Corporate retreats", desc: "Comfortably seats your entire team" });
              }
              if (vehicle.vehicle_type === "adventure" || vehicle.vauren_tier?.includes("IV")) {
                uses.push({ icon: Map, label: "Off-road adventures", desc: "Built for terrain that others can't handle" });
              }
              if (vehicle.booking_mode === "instant") {
                uses.push({ icon: Zap, label: "Airport transfers", desc: "Instant booking, no waiting for approval" });
              }
              if (vehicle.delivery_available) {
                uses.push({ icon: Package, label: "Events and occasions", desc: "Delivered to your venue, ready when you are" });
              }
              uses.push({ icon: Route, label: "Weekend road trips", desc: "Premium comfort for the open road" });
              if (vehicle.passenger_capacity >= 6) {
                uses.push({ icon: Heart, label: "Family adventures", desc: "Space and comfort for everyone" });
              }
              const visibleUses = uses.slice(0, 4);
              return (
                <>
                  <Section title="This van is perfect for">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: 16,
                      }}
                    >
                      {visibleUses.map((u, i) => {
                        const Icon = u.icon;
                        return (
                          <motion.div
                            key={u.label}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.4, delay: i * 0.08, ease: "easeOut" }}
                            style={{
                              background: PALETTE.surface,
                              border: `1px solid ${PALETTE.border}`,
                              borderRadius: 12,
                              padding: 20,
                              display: "flex",
                              gap: 16,
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                background: PALETTE.bgInset,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                              aria-hidden
                            >
                              <Icon size={16} color={PALETTE.bronze} strokeWidth={1.5} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontFamily: SANS, fontSize: 15, fontWeight: 600, color: PALETTE.text }}>
                                {u.label}
                              </div>
                              <div style={{ fontFamily: SANS, fontSize: 13, color: PALETTE.textTaupe, marginTop: 4, lineHeight: 1.5 }}>
                                {u.desc}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </Section>
                  <Divider />
                </>
              );
            })()}

            {/* K. Vehicle specs strip — plain text, no boxes */}
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: 24,
                fontWeight: 600,
                color: PALETTE.text,
                margin: 0,
                marginBottom: 20,
              }}
            >
              {vehicle.year} {titleCase(vehicle.make)} {titleCase(vehicle.model)}
            </h2>
            <div
              className="vd-specs-strip"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0 32px",
                rowGap: 20,
              }}
            >
              {specStrip.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label}>
                    <div
                      style={{
                        fontFamily: SERIF,
                        fontSize: 18,
                        fontWeight: 600,
                        color: PALETTE.text,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {Icon && <Icon size={16} color={PALETTE.bronze} />}
                      {s.value}
                    </div>
                    <div
                      style={{
                        fontFamily: SANS,
                        fontSize: 11,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: PALETTE.textTaupe,
                        marginTop: 2,
                      }}
                    >
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>

            <Divider />

            {/* O. Explore Dallas — curated local guide with external links.
                Full-width heading/intro, then a 2-column grid of spots
                (single column on mobile via .vd-explore-grid). */}
            {(() => {
              const isDallas = vehicle.city?.toLowerCase().includes("dallas");
              if (!isDallas) return null;
              const DALLAS_SPOTS = [
                { category: "Dining", name: "Monarch", desc: "Rooftop fine dining on the 49th floor with sweeping city views.", url: "https://monarchdallas.com" },
                { category: "Dining", name: "Bullion", desc: "French-American cuisine in an art deco gold vault setting.", url: "https://bullion-dallas.com" },
                { category: "Culture", name: "Nasher Sculpture Center", desc: "World-class sculpture garden in the heart of the Arts District.", url: "https://nashersculpturecenter.org" },
                { category: "Music", name: "Deep Ellum", desc: "Dallas's most storied neighbourhood for live music and late nights.", url: "https://deepellumtexas.com" },
                { category: "Cocktails", name: "Parliament", desc: "Intimate craft cocktail bar with an encyclopaedic menu.", url: "https://parliamentdallas.com" },
                { category: "Culture", name: "Perot Museum of Nature and Science", desc: "Five floors of immersive natural history and science exhibitions.", url: "https://perotmuseum.org" },
                { category: "Shopping", name: "Knox-Henderson", desc: "Independent boutiques, design studios, and neighbourhood dining.", url: "https://www.knoxhenderson.com" },
                { category: "Outdoors", name: "Katy Trail", desc: "A 3.5-mile urban trail through Uptown, ideal for morning runs.", url: "https://katytraildallas.org" },
              ];
              return (
                <>
                  <div className="vd-explore">
                    <h2
                      style={{
                        fontFamily: SERIF,
                        fontSize: 24,
                        fontWeight: 600,
                        color: PALETTE.text,
                        margin: 0,
                        marginBottom: 10,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      Explore Dallas
                    </h2>
                    <p
                      style={{
                        fontFamily: SANS,
                        fontSize: 14,
                        color: PALETTE.textTaupe,
                        margin: 0,
                        marginBottom: 28,
                        maxWidth: 600,
                        lineHeight: 1.6,
                      }}
                    >
                      A curated selection of Dallas experiences worth your time, from acclaimed dining to live music and cultural landmarks.
                    </p>
                    <div
                      className="vd-explore-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "0 48px",
                      }}
                    >
                      {DALLAS_SPOTS.map((spot, i) => {
                        const isHovered = hoveredSpot === i;
                        return (
                          <a
                            key={spot.url}
                            href={spot.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={() => setHoveredSpot(i)}
                            onMouseLeave={() => setHoveredSpot(null)}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "14px 0",
                              borderBottom: "1px solid #F0EBE6",
                              textDecoration: "none",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <span
                              style={{
                                fontFamily: SANS,
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: PALETTE.bronze,
                                marginBottom: 4,
                              }}
                            >
                              {spot.category}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: SERIF,
                                  fontSize: 17,
                                  fontWeight: 600,
                                  color: isHovered ? PALETTE.bronze : PALETTE.text,
                                  transition: "color 0.15s ease",
                                }}
                              >
                                {spot.name}
                              </span>
                              <ArrowUpRight
                                size={14}
                                color={isHovered ? PALETTE.bronze : PALETTE.textTaupe}
                                style={{ flexShrink: 0, transition: "color 0.15s ease" }}
                              />
                            </div>
                            <span
                              style={{
                                fontFamily: SANS,
                                fontSize: 13,
                                color: PALETTE.textTaupe,
                                lineHeight: 1.5,
                              }}
                            >
                              {spot.desc}
                            </span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  <Divider />
                </>
              );
            })()}

            {/* Q. Calendar / availability — days, not nights (van rental, not
                a hotel stay), always starts with a capital letter. */}
            <Section
              title={
                days > 0
                  ? `${days} ${days === 1 ? "day" : "days"} in ${vehicle.city}`
                  : "Select your dates"
              }
              subtitle={
                dateRange?.from && dateRange?.to
                  ? `${format(dateRange.from, "MMM d")} to ${format(dateRange.to, "MMM d, yyyy")}`
                  : "Choose pickup and return to see your total"
              }
            >
              <div
                className="vauren-calendar"
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                  showOutsideDays={false}
                  disabled={[{ before: new Date() }, isDateBooked]}
                  modifiers={{ booked: isDateBooked }}
                  onDayClick={handleDayClick}
                />
              </div>
            </Section>

            <Divider />

            {/* S. Reviews */}
            <Section title="Guest reviews">
              <VehicleReviews vehicleId={vehicle.id} />
            </Section>

            <Divider />

            {/* U. Where you'll be — wrapped with .vd-where-section so the
                mobile CSS rule guarantees the block stays visible, and the
                map container has .vd-map-container so RESPONSIVE_CSS can
                resize it to 300px on small viewports. */}
            <div className="vd-where-section">
            <Section
              title="Where you'll be"
              subtitle={`${pickupCity}, United States`}
            >
              {MAPBOX_TOKEN ? (
                <div
                  className="vd-map-container"
                  ref={setMapContainer}
                  style={{
                    // Explicit width/height/minHeight + position:relative so the
                    // mapbox-gl canvas always gets a non-zero sizing rect on
                    // first measure — a flex/grid ancestor that collapsed the
                    // box to 0 was the prior "blank tile" symptom.
                    width: "100%",
                    height: 480,
                    minHeight: 480,
                    position: "relative",
                    borderRadius: 12,
                    overflow: "hidden",
                    border: `1px solid ${PALETTE.border}`,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: 480,
                    background: PALETTE.bgInset,
                    borderRadius: 12,
                    border: `1px solid ${PALETTE.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    fontFamily: SANS,
                    color: PALETTE.textTaupe,
                  }}
                >
                  <div style={{ fontSize: 15, color: PALETTE.text }}>{pickupCity}</div>
                  <div style={{ fontSize: 13 }}>Map unavailable</div>
                </div>
              )}
              <div
                style={{
                  marginTop: 12,
                  fontFamily: SANS,
                  fontSize: 13,
                  color: PALETTE.textTaupe,
                }}
              >
                Exact location provided after booking
              </div>

              {/* Delivery summary — three shapes per the new
                  delivery_option enum. Falls back to the legacy
                  delivery_available boolean for listings that
                  predate the migration. */}
              {(() => {
                const opt =
                  vehicle.delivery_option ||
                  (vehicle.delivery_available ? "delivery_available" : "pickup_only");
                const fee = Number(vehicle.delivery_fee || 0);
                const cities = (vehicle.delivery_cities || []).filter(Boolean);
                const citySummary = cities.length > 0 ? cities.slice(0, 4).join(", ") : null;
                let line: React.ReactNode = null;
                if (opt === "pickup_only") {
                  line = (
                    <>
                      <strong style={{ color: PALETTE.text, fontWeight: 600 }}>
                        Pickup only
                      </strong>
                      {": "}
                      {vehicle.pickup_address || pickupCity}
                    </>
                  );
                } else if (opt === "delivery_available") {
                  line = (
                    <>
                      <strong style={{ color: PALETTE.text, fontWeight: 600 }}>
                        Delivery available
                      </strong>
                      {fee > 0 ? `: $${fee.toLocaleString()} fee` : ""}
                      {citySummary ? `. Serving: ${citySummary}` : ""}
                    </>
                  );
                } else if (opt === "both") {
                  line = (
                    <>
                      <strong style={{ color: PALETTE.text, fontWeight: 600 }}>
                        Pickup or delivery available
                      </strong>
                      {fee > 0 ? `: delivery $${fee.toLocaleString()}` : ""}
                      {citySummary ? `. Serving: ${citySummary}` : ""}
                    </>
                  );
                }
                return (
                  <div
                    style={{
                      marginTop: 14,
                      padding: "14px 16px",
                      background: PALETTE.bgInset,
                      border: `1px solid ${PALETTE.border}`,
                      borderRadius: 10,
                      fontFamily: SANS,
                      fontSize: 14,
                      color: PALETTE.textMuted,
                      lineHeight: 1.65,
                    }}
                  >
                    {line}
                    {vehicle.delivery_notes && opt !== "pickup_only" && (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 13,
                          color: PALETTE.textTaupe,
                        }}
                      >
                        {vehicle.delivery_notes}
                      </div>
                    )}
                  </div>
                );
              })()}
            </Section>
            </div>

            <Divider />

            {/* The Vauren Experience — universal trust block. Three
                standard reassurances every listing carries (detailed
                van, 24h handoff, inspected before rental) plus an
                optional fourth row when the host has filled in
                `pre_trip_message`. No host input required to render
                the standard rows. */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              style={{ marginBottom: 32 }}
            >
              <p
                style={{
                  fontFamily: SANS,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: PALETTE.bronze,
                  margin: "0 0 8px",
                }}
              >
                The Vauren Experience
              </p>
              <h3
                style={{
                  fontFamily: SERIF,
                  fontSize: 24,
                  fontWeight: 500,
                  color: PALETTE.text,
                  margin: "0 0 16px",
                  letterSpacing: "0.005em",
                  lineHeight: 1.2,
                }}
              >
                Your arrival, handled.
              </h3>
              <div
                style={{
                  background: "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  border: "1px solid rgba(153,126,84,0.15)",
                  borderRadius: 16,
                  padding: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                {[
                  {
                    Icon: CheckCircle,
                    copy:
                      "Your van will be detailed, fuelled, and ready at the agreed time.",
                  },
                  {
                    Icon: Bell,
                    copy:
                      "You will receive full handoff details 24 hours before your trip begins.",
                  },
                  {
                    Icon: Shield,
                    copy:
                      "Every Vauren van is inspected and verified before each rental.",
                  },
                  ...((vehicle as any).pre_trip_message &&
                  String((vehicle as any).pre_trip_message).trim()
                    ? [
                        {
                          Icon: MessageSquare,
                          copy: String((vehicle as any).pre_trip_message).trim(),
                        },
                      ]
                    : []),
                ].map(({ Icon, copy }, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", alignItems: "flex-start", gap: 12 }}
                  >
                    <Icon
                      size={18}
                      color={PALETTE.bronze}
                      strokeWidth={1.7}
                      style={{ flexShrink: 0, marginTop: 1 }}
                    />
                    <p
                      style={{
                        fontFamily: SANS,
                        fontSize: 14,
                        color: "rgba(44,36,32,0.8)",
                        margin: 0,
                        lineHeight: 1.55,
                      }}
                    >
                      {copy}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            <Divider />

            {/* V. Cancellation policy — pulls from
                describePolicy(vehicle.cancellation_policy, vehicle.custom_cancellation_policy).
                Dates are computed off `today` so renters see absolute
                cutoffs ("Full refund until Sun, May 25") instead of
                hours-from-pickup math. */}
            <Section title="Cancellation policy">
              {(() => {
                const desc = describePolicy({
                  now: new Date(),
                  pickupAt: null,
                  standardPolicy: vehicle.cancellation_policy,
                  customPolicy: vehicle.custom_cancellation_policy,
                });
                return (
                  <div
                    style={{
                      background: PALETTE.bgInset,
                      border: `1px solid ${PALETTE.border}`,
                      borderRadius: 12,
                      padding: 20,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 12,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: SANS,
                          fontSize: 10,
                          letterSpacing: "0.2em",
                          textTransform: "uppercase",
                          color: PALETTE.bronze,
                          fontWeight: 600,
                        }}
                      >
                        {vehicle.custom_cancellation_policy ? "Custom policy" : "Standard policy"}
                      </span>
                      <span
                        style={{
                          fontFamily: SERIF,
                          fontSize: 18,
                          fontWeight: 600,
                          color: PALETTE.text,
                        }}
                      >
                        {desc.name}
                      </span>
                    </div>
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {desc.bullets.map((b, i) => (
                        <li
                          key={i}
                          style={{
                            fontFamily: SANS,
                            fontSize: 14,
                            color: PALETTE.textMuted,
                            lineHeight: 1.65,
                            paddingLeft: 18,
                            position: "relative",
                          }}
                        >
                          <span
                            aria-hidden
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 9,
                              width: 8,
                              height: 1,
                              background: PALETTE.bronze,
                            }}
                          />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div
                      style={{
                        marginTop: 14,
                        fontFamily: SANS,
                        fontSize: 12,
                        color: PALETTE.textTaupe,
                        lineHeight: 1.6,
                      }}
                    >
                      Platform service fee (8%) is non-refundable. Security
                      deposit holds are released automatically on cancellation.
                    </div>
                  </div>
                );
              })()}
            </Section>

            <Divider />

            {/* W. Meet your host */}
            <Section title="Meet your host">
              <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: owner?.avatar_url ? PALETTE.bgInset : PALETTE.bronze,
                    overflow: "hidden",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: PALETTE.surface,
                    fontFamily: SANS,
                    fontSize: 22,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                  aria-label={owner?.display_name ? `${owner.display_name} avatar` : "Host avatar"}
                >
                  {owner?.avatar_url ? (
                    <img
                      src={owner.avatar_url}
                      alt={owner.display_name}
                      loading="lazy"
                      decoding="async"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : initials(owner?.display_name) ? (
                    <span>{initials(owner?.display_name)}</span>
                  ) : (
                    <Users size={26} color={PALETTE.surface} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div
                    style={{
                      fontFamily: SERIF,
                      fontSize: 22,
                      fontWeight: 600,
                      color: PALETTE.text,
                      marginBottom: 4,
                    }}
                  >
                    {firstName(owner?.display_name)
                      ? `Hosted by ${firstName(owner?.display_name)}`
                      : "Hosted by your host"}
                  </div>
                  {memberSince && (
                    <div
                      style={{
                        fontFamily: SANS,
                        fontSize: 13,
                        color: PALETTE.textTaupe,
                        marginBottom: 4,
                      }}
                    >
                      Hosting since {memberSince}
                    </div>
                  )}
                  {owner && owner.completed_trips > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 4,
                      }}
                    >
                      <Star size={13} color={PALETTE.bronze} fill={PALETTE.bronze} />
                      <span
                        style={{
                          fontFamily: SANS,
                          fontSize: 13,
                          color: PALETTE.textMuted,
                        }}
                      >
                        {owner.completed_trips} trip{owner.completed_trips !== 1 ? "s" : ""} hosted
                      </span>
                    </div>
                  )}
                  {owner && owner.average_rating != null && owner.average_rating > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 4,
                        marginBottom: 12,
                      }}
                    >
                      <Star size={13} color={PALETTE.bronze} fill={PALETTE.bronze} />
                      <span
                        style={{
                          fontFamily: SANS,
                          fontSize: 13,
                          color: PALETTE.textMuted,
                        }}
                      >
                        {owner.average_rating.toFixed(1)} average rating
                      </span>
                    </div>
                  )}
                  {hostBadges.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <TrustBadges badges={hostBadges} size="sm" />
                    </div>
                  )}
                  {owner?.bio && (
                    <p
                      style={{
                        fontFamily: SANS,
                        fontSize: 14,
                        color: PALETTE.textMuted,
                        lineHeight: 1.6,
                        margin: "0 0 14px",
                      }}
                    >
                      {owner.bio}
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => {
                        // Anonymous → bounce to auth and come back to the
                        // listing. Authenticated → open the inline composer
                        // modal so users never leave the listing context.
                        if (!currentUser) {
                          navigate(
                            `/marketplace/auth?redirect=${encodeURIComponent(window.location.pathname)}`,
                          );
                          return;
                        }
                        if (!owner) {
                          toast.error("Host details unavailable. Please refresh and try again.");
                          return;
                        }
                        setMessageModalOpen(true);
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: 0,
                        background: "none",
                        border: "none",
                        fontFamily: SANS,
                        fontSize: 14,
                        fontWeight: 600,
                        color: PALETTE.text,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      <MessageSquare size={14} />
                      Message host
                    </button>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontFamily: SANS,
                        fontSize: 12,
                        color: PALETTE.textTaupe,
                      }}
                    >
                      <Clock size={12} />
                      Typically responds within a day
                    </span>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* RIGHT COLUMN — STICKY BOOKING WIDGET */}
          <motion.aside
            className="vd-booking-widget"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
            style={{
              position: "sticky",
              top: 96,
              alignSelf: "start",
              maxHeight: "calc(100vh - 120px)",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                position: "relative",
                background: PALETTE.surface,
                border: `1px solid ${PALETTE.border}`,
                borderRadius: 12,
                padding: 24,
                boxShadow:
                  "0 1px 3px rgba(44,36,32,0.04), 0 8px 32px rgba(44,36,32,0.08)",
                overflow: "hidden",
              }}
            >
              {/* Bronze accent line — premium signal at the widget edge */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, transparent, ${PALETTE.bronze}, transparent)`,
                }}
              />
              {/* Price line */}
              <div style={{ marginBottom: 16 }}>
                <span
                  style={{
                    fontFamily: SERIF,
                    fontSize: 24,
                    fontWeight: 600,
                    color: PALETTE.text,
                  }}
                >
                  {formatCurrency(vehicle.daily_price)}
                </span>
                <span
                  style={{
                    fontFamily: SANS,
                    fontSize: 14,
                    color: PALETTE.textTaupe,
                    marginLeft: 4,
                  }}
                >
                  /night
                </span>
              </div>

              {/* Date cells (single Popover, two visual cells) */}
              <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    style={{
                      width: "100%",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      background: "transparent",
                      border: `1px solid ${PALETTE.border}`,
                      borderRadius: "8px 8px 0 0",
                      padding: 0,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    aria-label="Choose dates"
                  >
                    <div
                      style={{
                        padding: 12,
                        borderRight: `1px solid ${PALETTE.border}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: SANS,
                          fontSize: 10,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: PALETTE.textTaupe,
                          marginBottom: 4,
                        }}
                      >
                        Check-in
                      </div>
                      <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.text }}>
                        {dateRange?.from ? format(dateRange.from, "MMM d, yyyy") : "Add date"}
                      </div>
                    </div>
                    <div style={{ padding: 12 }}>
                      <div
                        style={{
                          fontFamily: SANS,
                          fontSize: 10,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: PALETTE.textTaupe,
                          marginBottom: 4,
                        }}
                      >
                        Checkout
                      </div>
                      <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.text }}>
                        {dateRange?.to ? format(dateRange.to, "MMM d, yyyy") : "Add date"}
                      </div>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <div className="vauren-calendar">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={1}
                      showOutsideDays={false}
                      disabled={[{ before: new Date() }, isDateBooked]}
                      modifiers={{ booked: isDateBooked }}
                      onDayClick={handleDayClick}
                    />
                  </div>
                </PopoverContent>
              </Popover>

              {/* Guests cell */}
              <div
                style={{
                  width: "100%",
                  border: `1px solid ${PALETTE.border}`,
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: 12,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontFamily: SANS,
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: PALETTE.textTaupe,
                    marginBottom: 4,
                  }}
                >
                  Guests
                </div>
                <div style={{ fontFamily: SANS, fontSize: 14, color: PALETTE.text }}>
                  Up to {vehicle.passenger_capacity} guests
                </div>
              </div>

              {/* Social-proof urgency — concurrent viewers + lifetime booking count */}
              {viewerCount >= 3 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    background: "#FFF8E8",
                    borderRadius: 8,
                    border: "1px solid rgba(153,126,84,0.2)",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: PALETTE.bronze,
                      animation: "pulse 2s infinite",
                    }}
                  />
                  <span style={{ fontFamily: SANS, fontSize: 13, color: PALETTE.textMuted }}>
                    {viewerCount} people viewing this van right now
                  </span>
                </div>
              )}
              {confirmedBookingCount >= 3 && (
                <p
                  style={{
                    fontFamily: SANS,
                    fontSize: 13,
                    color: PALETTE.textTaupe,
                    textAlign: "center",
                    margin: "0 0 12px",
                  }}
                >
                  ⭐ Booked {confirmedBookingCount} times
                </p>
              )}

              {/* Reserve button */}
              <button
                onClick={() => {
                  if (!dateRange?.from || !dateRange?.to) {
                    toast.error("Please select trip dates");
                    return;
                  }
                  const startStr = format(dateRange.from, "yyyy-MM-dd");
                  const endStr = format(dateRange.to, "yyyy-MM-dd");
                  // META PIXEL: InitiateCheckout — verify in Meta Events Manager > Test Events
                  trackInitiateCheckout({
                    content_ids: [vehicle.id],
                    num_items: 1,
                    value: total > 0 ? total : vehicle.daily_price,
                    currency: "USD",
                  });
                  // Persist the booking intent before navigating. If the
                  // renter is unauthenticated, BookVehicle's auth gate
                  // will redirect through /marketplace/auth (and
                  // possibly Supabase email verification) — the URL
                  // params get lost on that round trip, so the saved
                  // intent in sessionStorage is what lets us land the
                  // renter back on the dated booking page after auth.
                  saveBookingIntent({
                    vehicleId: vehicle.id,
                    startDate: startStr,
                    endDate: endStr,
                    totalPrice: total > 0 ? total : null,
                    vehicleSlug: vehicle.title || null,
                  });
                  navigate(
                    `/marketplace/book/${vehicle.id}?from=${startStr}&to=${endStr}`,
                  );
                }}
                style={{
                  width: "100%",
                  padding: "14px 18px",
                  background: PALETTE.bronze,
                  color: PALETTE.surface,
                  border: "none",
                  borderRadius: 8,
                  fontFamily: SERIF,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.2s ease",
                }}
                onMouseOver={(e) => ((e.currentTarget as HTMLButtonElement).style.background = PALETTE.bronzeDark)}
                onMouseOut={(e) => ((e.currentTarget as HTMLButtonElement).style.background = PALETTE.bronze)}
              >
                {dateRange?.from && dateRange?.to ? "Reserve" : "Check availability"}
              </button>

              <div
                style={{
                  marginTop: 12,
                  fontFamily: SANS,
                  fontSize: 13,
                  color: PALETTE.textTaupe,
                  textAlign: "center",
                }}
              >
                You won't be charged yet
              </div>

              {/* Pricing breakdown — plain rows only when dates picked */}
              {days > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: SANS,
                      fontSize: 14,
                      color: PALETTE.text,
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ textDecoration: "underline" }}>
                      {formatCurrency(vehicle.daily_price)} × {days} {days === 1 ? "night" : "nights"}
                    </span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontFamily: SANS,
                        fontSize: 14,
                        color: PALETTE.text,
                        marginBottom: 12,
                      }}
                    >
                      <span>{discountPercent}% multi-day discount</span>
                      <span>-{formatCurrency(discountAmount)}</span>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: SANS,
                      fontSize: 14,
                      color: PALETTE.text,
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ textDecoration: "underline" }}>Cleaning fee</span>
                    <span>{formatCurrency(vehicle.cleaning_fee)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: SANS,
                      fontSize: 14,
                      color: PALETTE.text,
                      marginBottom: 12,
                    }}
                  >
                    <span>Vauren service fee ({Math.round(VAUREN_SERVICE_FEE_RATE * 100)}%)</span>
                    <span>{formatCurrency(serviceFee)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: SANS,
                      fontSize: 14,
                      color: PALETTE.text,
                      marginBottom: 4,
                    }}
                  >
                    <span>Security deposit</span>
                    <span>{formatCurrency(securityDeposit)}</span>
                  </div>
                  <div
                    style={{
                      fontFamily: SANS,
                      fontSize: 12,
                      color: PALETTE.textMuted,
                      lineHeight: 1.5,
                      marginBottom: 16,
                    }}
                  >
                    Refunded within 5 business days if no damage is reported.
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontFamily: SANS,
                      fontSize: 15,
                      color: PALETTE.text,
                      fontWeight: 600,
                      paddingTop: 16,
                      borderTop: `1px solid ${PALETTE.border}`,
                    }}
                  >
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Report this listing — outside widget */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
              <button
                onClick={() => toast.message("Report received. Our team will review this listing.")}
                onMouseOver={(e) => ((e.currentTarget as HTMLButtonElement).style.textDecoration = "underline")}
                onMouseOut={(e) => ((e.currentTarget as HTMLButtonElement).style.textDecoration = "none")}
                style={{
                  background: "none",
                  border: "none",
                  fontFamily: SANS,
                  fontSize: 13,
                  color: PALETTE.text,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Flag size={13} />
                Report this listing
              </button>
            </div>
          </motion.aside>
        </div>
      </div>

      {lightboxIndex != null && (
        <Lightbox photos={photos} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}

      {messageModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
          onClick={() => setMessageModalOpen(false)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: 32,
              maxWidth: 480,
              width: "100%",
              boxShadow: "0 20px 60px rgba(44,36,32,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: 22,
                  fontWeight: 600,
                  color: "#2C2420",
                  margin: 0,
                  letterSpacing: "-0.005em",
                }}
              >
                Message {firstName(owner?.display_name) || "the host"}
              </h3>
              <button
                onClick={() => setMessageModalOpen(false)}
                aria-label="Close"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9C9088",
                  fontSize: 20,
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={`Hi ${firstName(owner?.display_name) || "there"}, I have a question about your van...`}
              rows={5}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #E0D8D2",
                borderRadius: 8,
                fontFamily: '"DM Sans", system-ui, sans-serif',
                fontSize: 15,
                color: "#2C2420",
                background: "#FFFFFF",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
                marginBottom: 16,
                lineHeight: 1.6,
              }}
            />

            <button
              disabled={!messageText.trim() || messageSending}
              onClick={async () => {
                if (!messageText.trim() || !currentUser || !owner) return;
                setMessageSending(true);
                try {
                  // marketplace_messages is conversation-keyed; find or create
                  // a conversation between the current user and the host first.
                  const { data: existing, error: cErr } = await supabase
                    .from("conversations")
                    .select("id")
                    .or(
                      `and(participant_one.eq.${currentUser.id},participant_two.eq.${owner.user_id}),and(participant_one.eq.${owner.user_id},participant_two.eq.${currentUser.id})`,
                    )
                    .maybeSingle();
                  if (cErr) throw cErr;

                  let conversationId = existing?.id;
                  if (!conversationId) {
                    const { data: created, error: createErr } = await supabase
                      .from("conversations")
                      .insert({
                        participant_one: currentUser.id,
                        participant_two: owner.user_id,
                      })
                      .select("id")
                      .single();
                    if (createErr) throw createErr;
                    conversationId = created.id;
                  }

                  const { error: mErr } = await supabase
                    .from("marketplace_messages")
                    .insert({
                      conversation_id: conversationId,
                      sender_id: currentUser.id,
                      content: messageText.trim(),
                    });
                  if (mErr) throw mErr;

                  toast.success("Message sent");
                  setMessageModalOpen(false);
                  setMessageText("");
                } catch (err) {
                  console.error("[VehicleDetail] message send failed", err);
                  toast.error("Failed to send message. Please try again.");
                } finally {
                  setMessageSending(false);
                }
              }}
              style={{
                width: "100%",
                padding: "14px 24px",
                background: messageText.trim() ? "#997E54" : "#E0D8D2",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 8,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 16,
                fontWeight: 600,
                cursor: messageText.trim() ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
              }}
            >
              {messageSending ? "Sending..." : "Send message"}
            </button>

            <p
              style={{
                fontSize: 13,
                color: "#9C9088",
                textAlign: "center",
                marginTop: 12,
                marginBottom: 0,
                fontFamily: '"DM Sans", system-ui, sans-serif',
              }}
            >
              The host typically responds within 24 hours
            </p>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default VehicleDetail;
